import React, { useEffect, useRef } from 'react';
import { Box, Button, Input, Link, Stack, StatusMessage, useFloatingAlert } from '@99designs/emu';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import {
    getSupportArticleUrl,
    getSupportContactUrl,
} from '@99designs/frontend-components/utils/platform/supportUrlHelpers';
import { ResendTokenPrompt } from './ResendTokenPrompt';
import { ErrorType, UIStateType } from './types';

interface VerifyLoginFormProps {
    inputError: ErrorType | null;
    onSubmit: (token: string) => void;
    requestNewToken: () => void;
    resendRequestCount: number;
    uiState: UIStateType;
}

const errorMessages: { [type in ErrorType]: string } = {
    TOKEN_ATTEMPTS: __('Too many failed attempts. Please <Link>request a new code</Link>'),
    TOKEN_EMPTY: __('Please enter your login code'),
    TOKEN_EXPIRED: __('This code is no longer valid. Please <Link>request a new code</Link>'),
    TOKEN_INVALID: __('Your code is invalid. Please try again'),
};

export function VerifyLoginForm(props: VerifyLoginFormProps) {
    const { inputError, onSubmit, requestNewToken, resendRequestCount, uiState } = props;

    const inputEl = useRef<HTMLInputElement>(null);
    const floatingAlert = useFloatingAlert();

    useEffect(() => {
        if (uiState === 'ERROR' && inputError === null) {
            floatingAlert({
                type: 'error',
                message: (
                    <FormattedReactMessage
                        message={__('Something went wrong, please <Link>contact support</Link>')}
                        replacements={{
                            Link: (
                                <Link
                                    color="white"
                                    weight="bold"
                                    href={getSupportContactUrl(__LANG__)}
                                />
                            ),
                        }}
                    />
                ),
            });
        } else if (uiState === 'SUCCESS' || uiState === 'RESEND_SUCCESS') {
            floatingAlert({
                type: 'success',
                message:
                    uiState === 'RESEND_SUCCESS'
                        ? __('Code re-sent')
                        : __('Code accepted. Redirecting... '),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputError, uiState]);

    useEffect(() => {
        if (inputEl.current) {
            inputEl.current.focus();
        }
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const t = inputEl?.current ? inputEl.current.value : '';
        onSubmit(t);
    };

    return (
        <Stack space="1bu">
            <form onSubmit={handleSubmit}>
                <Stack space="2bu">
                    <CodeInputField
                        ref={inputEl}
                        error={
                            uiState === 'ERROR' && inputError ? (
                                <FormattedReactMessage
                                    message={errorMessages[inputError]}
                                    replacements={{
                                        Link: (
                                            <Link
                                                data-testid="resend-token-error"
                                                color="risingSun"
                                                onClick={requestNewToken}
                                            />
                                        ),
                                    }}
                                />
                            ) : undefined
                        }
                    />
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="baseline"
                        justifyContent="center"
                    >
                        <Button
                            data-testid="verify-token"
                            appearance="primary"
                            width="fill"
                            loading={uiState === 'LOADING'}
                            disabled={uiState === 'LOADING' || uiState === 'RESEND_LOADING'}
                            type="submit"
                        >
                            {__('Log in')}
                        </Button>
                    </Box>
                </Stack>
            </form>
            <ResendTokenPrompt
                requestNewToken={requestNewToken}
                resendRequestCount={resendRequestCount}
            />
            <Box display="flex" flexDirection="row" alignItems="baseline" justifyContent="center">
                <Box whiteSpace="nowrap">
                    <Link href={getSupportArticleUrl('360044399191', __LANG__)}>
                        {__('Why am I seeing this?')}
                    </Link>
                </Box>
            </Box>
        </Stack>
    );
}

const CodeInputField = React.forwardRef(function (
    {
        error,
    }: {
        error?: React.ReactNode;
    },
    ref: React.RefObject<HTMLInputElement>
) {
    return (
        <Stack space="1bu">
            {!!error && (
                <StatusMessage type="error">
                    <span data-testid="error-message">{error}</span>
                </StatusMessage>
            )}
            <Input
                error={!!error}
                ref={ref}
                aria-label={__('Enter login code')}
                placeholder={__('Login code')}
                id="verification-token"
                name="token"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                autoComplete="one-time-code"
            />
        </Stack>
    );
});
